/**
 * InlineProposal — orchestrator for collapsed/expanded inline proposal states.
 *
 * Manages the expand/collapse state machine with:
 * - Smart initial state based on resolution and action count
 * - isFresh override (newly-created proposals always start expanded)
 * - Post-approval auto-collapse with 2s delay (respects prefers-reduced-motion)
 * - Lazy loading of InlineProposalExpanded via React.lazy
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { InlineEditableContentWrapper } from '@/lib/components/InlineEditableContentWrapper';
import { LazyRouteBoundary } from '@/lib/components/LazyRouteBoundary';

import { InlineProposalCard } from './InlineProposalCard';

// ─── Lazy load ────────────────────────────────────────────────────────────────

const InlineProposalExpanded = React.lazy(() =>
  import('./InlineProposalExpanded').then(m => ({
    default: m.InlineProposalExpanded,
  }))
);

// ─── Smart default ────────────────────────────────────────────────────────────

/**
 * Determine whether a proposal should start expanded based on its resolution
 * and action count. Expands by default for pending proposals unless the action
 * count is known to be complex (3+); everything else starts collapsed.
 */
function smartDefault(
  resolution: string,
  actionCount: number | undefined
): boolean {
  // Only pending proposals can start expanded; resolved ones always collapse.
  if (resolution !== 'pending') {
    return false;
  }
  // When actionCount is unknown (undefined), treat as simple (not complex) and
  // expand. Only collapse when we know there are 3+ actions.
  return actionCount === undefined || actionCount <= 2;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface InlineProposalProps {
  proposalId: number;
  orgId: number;
  title: string;
  resolution: string;
  isFresh: boolean;
  actionCount?: number;
  /**
   * True inside a builder conversation, where clicking a card selects it as
   * the panel's active draft rather than approving/rejecting it — Save in
   * the panel is the only apply path there, so this card must not offer a
   * second one.
   */
  isBuilderMode?: boolean;
  /** Whether this is the proposal currently driving the builder panel. */
  isSelected?: boolean;
  /**
   * Selects this proposal as the builder panel's active draft. Called with
   * this card's own `proposalId`. Only meaningful when `isBuilderMode`.
   */
  onSelect?: (proposalId: number) => void;
}

/**
 * Whether a card starts expanded.
 *
 * A BUILDER card never does. It is a selector, not a reviewer: the panel
 * beside the chat already renders the full record, so expanding the proposal
 * inline would show the same change twice and push the conversation itself off
 * the screen — which is exactly what it did on a phone.
 */
function initialExpanded({
  isBuilderMode,
  isFresh,
  resolution,
  actionCount,
}: {
  isBuilderMode: boolean;
  isFresh: boolean;
  resolution: string;
  actionCount?: number;
}): boolean {
  if (isBuilderMode) {
    return false;
  }
  if (isFresh) {
    return true;
  }
  return smartDefault(resolution, actionCount);
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Orchestrates the collapsed/expanded lifecycle for a proposal embedded in a
 * chat message. The InlineProposalCard header is always rendered; the full
 * InlineProposalExpanded panel is lazy-loaded only when expanded.
 */
export function InlineProposal({
  proposalId,
  orgId,
  title,
  resolution,
  isFresh,
  actionCount,
  isBuilderMode = false,
  isSelected = false,
  onSelect,
}: InlineProposalProps) {
  const [isExpanded, setIsExpanded] = useState<boolean>(
    initialExpanded({ isBuilderMode, isFresh, resolution, actionCount })
  );

  // Track the previous resolution to detect pending → resolved transitions.
  const prevResolutionRef = useRef<string>(resolution);
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Post-approval/rejection auto-collapse with optional delay.
  useEffect(() => {
    const prevResolution = prevResolutionRef.current;
    prevResolutionRef.current = resolution;

    const wasResolved =
      prevResolution !== 'approved' &&
      prevResolution !== 'rejected' &&
      (resolution === 'approved' || resolution === 'rejected');

    if (!wasResolved) {
      return;
    }

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (prefersReducedMotion) {
      setIsExpanded(false);
    } else {
      collapseTimerRef.current = setTimeout(() => {
        setIsExpanded(false);
      }, 2000);
    }

    return () => {
      if (collapseTimerRef.current !== null) {
        clearTimeout(collapseTimerRef.current);
        collapseTimerRef.current = null;
      }
    };
  }, [resolution]);

  // Clean up any pending timer on unmount.
  useEffect(() => {
    return () => {
      if (collapseTimerRef.current !== null) {
        clearTimeout(collapseTimerRef.current);
      }
    };
  }, []);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const handleSelect = useCallback(() => {
    onSelect?.(proposalId);
  }, [onSelect, proposalId]);

  const handleApprovalComplete = useCallback(() => {
    // Resolution prop update will trigger auto-collapse via the effect above.
    // Nothing extra needed here.
  }, []);

  const handleEditDenied = useCallback(() => {
    setIsExpanded(false);
  }, []);

  return (
    <div className='flex flex-col gap-2'>
      <InlineProposalCard
        proposalId={proposalId}
        orgId={orgId}
        title={title}
        resolution={resolution}
        isExpanded={isExpanded}
        onToggleExpand={handleToggleExpand}
        isFresh={isFresh}
        isBuilderMode={isBuilderMode}
        isSelected={isSelected}
        onSelect={isBuilderMode ? handleSelect : undefined}
      />

      {/* Never inline the full proposal in builder mode — see initialExpanded.
          Gated here as well as in the initial state so no future toggle path
          can reintroduce it.
          containRenderErrors: a crashing proposal card must not take down the
          surrounding chat thread (pre-existing containment behavior). */}
      {isExpanded && !isBuilderMode && (
        <LazyRouteBoundary
          name={`inline-proposal-${proposalId}`}
          containRenderErrors
        >
          <InlineEditableContentWrapper
            editId={`proposal-${proposalId}`}
            skeletonHeight={200}
            onEditDenied={handleEditDenied}
          >
            <InlineProposalExpanded
              proposalId={proposalId}
              orgId={orgId}
              onApprovalComplete={handleApprovalComplete}
            />
          </InlineEditableContentWrapper>
        </LazyRouteBoundary>
      )}
    </div>
  );
}
